import { createContext, useContext, useCallback, useEffect, useState, ReactNode } from 'react';
import { useAuth } from '../../auth/Auth';
import { getUserPoints } from '../../auth/_requests';
import { userPointsResponse } from '../utils/types';

interface PointsContextProps {
  points: userPointsResponse | null;
  loading: boolean;
  refreshPoints: () => Promise<void>;
}

const PointsContext = createContext<PointsContextProps>({
  points: null,
  loading: false,
  refreshPoints: async () => {},
});

export const usePoints = () => useContext(PointsContext);

export const PointsProvider = ({ children }: { children: ReactNode }) => {
  const { auth } = useAuth();
  const [points, setPoints] = useState<userPointsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(() => Boolean(auth?.data.token));

  const fetchPoints = useCallback(async () => {
    if (!auth?.data.token) {
      setPoints(null);
      return;
    }
    setLoading(true);
    try {
      const response = await getUserPoints(auth.data.token);
      setPoints(response);
    } catch (error) {
      console.error('Error fetching points for user:', error);
    } finally {
      setLoading(false);
    }
  }, [auth?.data.token]);

  useEffect(() => {
    void fetchPoints();
  }, [fetchPoints]);

  const refreshPoints = fetchPoints;

  return (
    <PointsContext.Provider value={{ points, loading, refreshPoints }}>
      {children}
    </PointsContext.Provider>
  );
};
